import { useEffect, useMemo, useState } from 'react';
import { api, type GrpScope, type ScreenKey, type ScreenLv, type ScreenPermChange, type ScreenPermTable } from '../lib/api';
import { SCREEN_LABEL } from '../lib/me';
import { ErrorBox } from '../components/DataTable';

/**
 * 화면 권한 — 팀·개인 예외(2026-09-27 way 「화면별로 팀별·개인별 권한, 슈퍼관리자가 조정」).
 *
 * 방식 = **역할 + 화면별 예외**. 칸을 비워 두면(「역할대로」·「팀·역할대로」) 위 단계를 따른다 —
 * 개인 > 팀 > 역할 순. 표가 비어 있으면 지금과 같다. 관리자는 늘 전부 입력이라 칸이 없다.
 * 최종 단계는 **서버가 계산해 준 값**을 그대로 보여 준다(화면이 판정을 복제하지 않는다).
 * 강제는 서버의 각 함수(`requireScreen`)가 한다 — 여기서 숨겨도 주소로 들어오면 서버가 막는다.
 *
 * 2026-10-13 way 「권한화면 통합 — 너무 산별적」: 개인별 표를 따로 두지 않고 사용자 관리 표의
 * 칸으로 넣는다(`Admin.tsx`). 이 파일은 상태(`useScreenPerms`)와 칸·팀별 표·저장 막대만 낸다.
 */

const LV_LABEL: Record<ScreenLv, string> = { none: '숨김', read: '조회', write: '입력' };
const LV_TONE: Record<ScreenLv, string> = { none: 'text-red-700', read: 'text-ink-soft', write: 'text-green-700' };
/** 기준정보 수정 소속(09-28 way 「영업팀은 외부 소속만」) — 기준정보 「입력」 위에 얹는 좁히기. 조회는 그대로. */
const GRP_LABEL: Record<GrpScope, string> = { all: '전체', internal: '내부만', external: '외부만' };
export const GRP_TITLE = '기준정보(운행편성 포함)를 고칠 수 있는 소속입니다. 보는 것은 모든 소속이 보입니다. 예) 인사팀·차량팀 = 내부만, 영업팀 = 외부만';

type Pending = Record<string, ScreenPermChange>;
type PermUser = ScreenPermTable['users'][number];
const keyOf = (c: ScreenPermChange) => `${c.team ?? ''}|${c.appUserId ?? ''}|${c.screen}`;

export type ScreenPerms = ReturnType<typeof useScreenPerms>;

/**
 * 화면 권한 표 상태. `nonce` 가 바뀌면(사용자 표에서 역할·팀을 바꿨을 때) 다시 읽되
 * **아직 저장 안 한 칸은 남긴다** — 예전엔 패널을 통째로 다시 붙여 바꾼 칸이 사라졌다.
 */
export function useScreenPerms(nonce: number) {
  const [data, setData] = useState<ScreenPermTable | null>(null);
  const [pending, setPending] = useState<Pending>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [own, setOwn] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.listScreenPerms()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => { cancelled = true; };
  }, [nonce, own]);

  const byUser = useMemo(() => new Map((data?.users ?? []).map((u) => [u.app_user_id, u])), [data]);
  const put = (c: ScreenPermChange) => {
    setDone(null);
    setPending((p) => ({ ...p, [keyOf(c)]: c }));
  };
  const pendingOf = (team: string | null, userId: number | null, s: ScreenKey | 'master-grp') =>
    pending[`${team ?? ''}|${userId ?? ''}|${s}`];

  async function save() {
    const changes = Object.values(pending);
    if (changes.length === 0) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const r = await api.setScreenPerms({ changes });
      setDone(`${r.changed}칸을 저장했습니다. 해당 직원은 새로고침하면 메뉴가 바뀝니다.`);
      setPending({});
      setOwn((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return {
    data, byUser, put, pendingOf, save, busy, error, done,
    screens: data?.screens ?? [],
    nPending: Object.keys(pending).length,
    discard: () => setPending({}),
  };
}

/** 사용자 표의 화면 칸 하나. 비활성 계정은 권한 표에 없어 「—」. */
export function UserScreenCell({ sp, userId, screen }: { sp: ScreenPerms; userId: number; screen: ScreenKey }) {
  const u = sp.byUser.get(userId);
  if (!u) return <span className="text-xs text-ink-faint">—</span>;
  if (u.role === 'admin') return <span className="text-xs text-green-700">입력</span>;
  const c = u.cells[screen];
  const p = sp.pendingOf(null, userId, screen);
  const cur = p ? (p.level === 'inherit' ? '' : p.level) : (c.own ?? '');
  return (
    <div className={`-m-1 rounded p-1 ${p ? 'bg-amber-100' : c.own ? 'bg-sky-50' : ''}`}>
      <select
        className="field w-[5.5rem] py-0.5 text-xs"
        value={cur}
        title="비워 두면 팀·역할대로"
        onChange={(e) => sp.put({ appUserId: userId, screen, level: (e.target.value || 'inherit') as ScreenLv | 'inherit' })}
      >
        <option value="">팀·역할대로</option>
        <option value="none">숨김</option>
        <option value="read">조회</option>
        <option value="write">입력</option>
      </select>
      <div className={`mt-0.5 text-[11px] ${LV_TONE[c.eff]}`}>
        {LV_LABEL[c.eff]}
        <span className="text-ink-faint">{c.own ? ' (개인)' : c.team ? ' (팀)' : ' (역할)'}</span>
      </div>
    </div>
  );
}

/** 사용자 표의 「기준정보 수정 소속」 칸. */
export function UserGrpCell({ sp, userId }: { sp: ScreenPerms; userId: number }) {
  const u: PermUser | undefined = sp.byUser.get(userId);
  if (!u) return <span className="text-xs text-ink-faint">—</span>;
  if (u.role === 'admin') return <span className="text-xs text-green-700">전체</span>;
  const g = u.grpScope;
  const p = sp.pendingOf(null, userId, 'master-grp');
  const cur = p ? (p.level === 'inherit' ? '' : p.level) : (g.own ?? '');
  return (
    <div className={`-m-1 rounded p-1 ${p ? 'bg-amber-100' : g.own ? 'bg-sky-50' : ''}`}>
      <select
        className="field w-[5.5rem] py-0.5 text-xs"
        value={cur}
        onChange={(e) => sp.put({ appUserId: userId, screen: 'master-grp', level: (e.target.value || 'inherit') as GrpScope | 'inherit' })}
      >
        <option value="">팀대로</option>
        <option value="all">전체</option>
        <option value="internal">내부만</option>
        <option value="external">외부만</option>
      </select>
      <div className="mt-0.5 text-[11px] text-ink-soft">
        {GRP_LABEL[g.eff]}
        <span className="text-ink-faint">{g.own ? ' (개인)' : g.team ? ' (팀)' : ' (기본)'}</span>
      </div>
      {/* 소속 제한은 쓰기 범위만 좁힌다 — 기준정보가 입력이 아니면 등록·수정 자체가 없다(10-29 dy@ 혼동). */}
      {g.eff !== 'all' && u.cells.master.eff !== 'write' && (
        <div className="mt-0.5 text-[11px] text-amber-700">⚠ 기준정보가 입력이 아니라 효과 없음</div>
      )}
    </div>
  );
}

/** 바꾼 칸이 있을 때 화면 아래에 붙는 저장 막대(사용자 표·팀별 표 공통). */
export function ScreenPermSaveBar({ sp }: { sp: ScreenPerms }) {
  if (sp.nPending === 0 && !sp.done && !sp.error) return null;
  return (
    <div className="sticky bottom-[calc(var(--bottom-bar,0px)+0.5rem)] z-20 space-y-2">
      {sp.error && <ErrorBox message={sp.error} />}
      {sp.done && sp.nPending === 0 && <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800 shadow">{sp.done}</div>}
      {sp.nPending > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm shadow">
          <span className="font-medium">화면 권한 {sp.nPending}칸을 바꿨습니다(노란 칸). 저장해야 적용됩니다.</span>
          <button className="btn-primary ml-auto" disabled={sp.busy} onClick={() => void sp.save()}>
            {sp.busy ? '저장 중…' : `${sp.nPending}칸 저장`}
          </button>
          <button className="btn-ghost" disabled={sp.busy} onClick={sp.discard}>되돌리기</button>
        </div>
      )}
    </div>
  );
}

/** 팀별 기본 권한 — 팀이 지정된 사람이 있어야 줄이 생긴다. */
export function TeamPermTable({ sp }: { sp: ScreenPerms }) {
  const { data, screens } = sp;
  if (!data) return <p className="text-sm text-ink-faint">불러오는 중…</p>;
  if (data.teams.length === 0) {
    return <p className="text-sm text-ink-faint">팀이 지정된 사용자가 없습니다. 아래 사용자 표에서 팀을 먼저 지정하세요.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr>
            <th className="th">팀</th>
            {screens.map((s) => <th key={s} className="th whitespace-nowrap">{SCREEN_LABEL[s]}</th>)}
            <th className="th whitespace-nowrap" title={GRP_TITLE}>기준정보 수정 소속</th>
          </tr>
        </thead>
        <tbody>
          {data.teams.map((t) => (
            <tr key={t.team}>
              <td className="td whitespace-nowrap font-medium">{t.team}</td>
              {screens.map((s) => {
                const p = sp.pendingOf(t.team, null, s);
                const cur = p ? (p.level === 'inherit' ? '' : p.level) : (t.levels[s] ?? '');
                return (
                  <td key={s} className={`td ${p ? 'bg-amber-100' : cur ? 'bg-sky-50' : ''}`}>
                    <select
                      className="field py-1 text-sm"
                      value={cur}
                      onChange={(e) => sp.put({ team: t.team, screen: s, level: (e.target.value || 'inherit') as ScreenLv | 'inherit' })}
                    >
                      <option value="">역할대로</option>
                      <option value="none">숨김</option>
                      <option value="read">조회</option>
                      <option value="write">입력</option>
                    </select>
                  </td>
                );
              })}
              {(() => {
                const p = sp.pendingOf(t.team, null, 'master-grp');
                const cur = p ? (p.level === 'inherit' ? '' : p.level) : (t.grpScope ?? '');
                const mp = sp.pendingOf(t.team, null, 'master');
                const master = mp ? (mp.level === 'inherit' ? '' : mp.level) : (t.levels.master ?? '');
                return (
                  <td className={`td ${p ? 'bg-amber-100' : cur ? 'bg-sky-50' : ''}`}>
                    <select
                      className="field py-1 text-sm"
                      value={cur}
                      onChange={(e) => sp.put({ team: t.team, screen: 'master-grp', level: (e.target.value || 'inherit') as GrpScope | 'inherit' })}
                    >
                      <option value="">전체(기본)</option>
                      <option value="internal">내부만</option>
                      <option value="external">외부만</option>
                    </select>
                    {cur && master !== 'write' && (
                      <div className="mt-0.5 whitespace-nowrap text-[11px] text-amber-700">
                        ⚠ 기준정보 {master === '' ? '역할대로' : master === 'read' ? '조회' : '숨김'} — 입력이어야 등록
                      </div>
                    )}
                  </td>
                );
              })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
